import React, { useCallback, useContext, useReducer, useEffect } from 'react'
import {
  getFirestore,
  collection,
  doc,
  getDocs,
  getDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  or
} from 'firebase/firestore'
import Spinner from '../../Spinner'
import { getAuth } from 'firebase/auth'
import { db, orgScopedUsersQuery } from '../../../firebase'
import { ApiContext, useApiRequest } from '../../../hooks/api'
import { fetchUserProfile } from '../../../hooks/userProfiles'

// ✅ Firestore instance
export const DispatchDataContext = React.createContext({})

export function useDispatchData() {
  return useContext(DispatchDataContext)
}

const REINIT_STREAM_EVENT = 'reinit'
const DEVICE_INFO_EVENT = 'info'
const DEVICE_LOCATION_EVENT = 'loc'

// -------------------- DEVICE LOCATIONS -------------------- //
function reduceLocations(state, { type, data }) {
  const { id, loc, user, timestamp } = data || {}

  switch (type) {
    case REINIT_STREAM_EVENT:
      // Remove specific user from state
      if (data.id) {
        const newState = { ...state }
        delete newState[data.id]
        return newState
      }
      // Clear all if no id specified
      return {}
    case DEVICE_INFO_EVENT:
    case DEVICE_LOCATION_EVENT:
      if (!id || !user) return state

      return {
        ...state,
        [id]: {
          loc: loc || [user.locationLon, user.locationLat],
          user: {
            ...user,
            // Ensure user has all required properties
            id: user.id || id,
            displayName: user.displayName || `User ${id}`,
            dutyStatus: user.dutyStatus !== undefined ? user.dutyStatus : true
          },
          timestamp: timestamp || new Date().toISOString()
        }
      }
    default:
      return { ...state }
  }
}

// function useDeviceLocations(organizationId) {
//   const [deviceLocations, dispatch] = useReducer(reduceLocations, {})

//   useEffect(() => {
//     if (!organizationId) return

//     const q = query(
//       collection(db, 'users'),
//       where('dutyStatus', '==', true),
//       where('organization.id', '==', organizationId) // Filter by organizationId
//     )

//     const unsub = onSnapshot(q, (snapshot) => {
//       snapshot.docChanges().forEach((change) => {
//         const user = change.doc.data()

//         if (user.locationLat != null && user.locationLon != null) {
//           dispatch({
//             type: DEVICE_LOCATION_EVENT,
//             data: {
//               id: change.doc.id,
//               loc: [user.locationLon, user.locationLat],
//               user: { id: change.doc.id, ...user }
//             }
//           })
//         }
//       })
//     })

//     return () => unsub()
//   }, [organizationId])

//   return deviceLocations
// }

// Replace the entire useDeviceLocations function with this:
function useDeviceLocations(organizationId) {
  const [deviceLocations, dispatch] = useReducer(reduceLocations, {})
  const { socket } = useContext(ApiContext)

  // Function to fetch user details from Firebase.
  //
  // DELIBERATELY NOT SCOPED TO THE ORGANISATION. It used to drop anyone whose
  // organization.id was not ours, which meant a responder from a dispatched
  // agency never entered deviceLocations at all -- selecting their incident
  // showed the incident pin and no crew. Scoping now happens once, in
  // Dispatch/index.jsx's mapDeviceLocations, which is also what decides between
  // "own organisation" and "this incident's responders". Two filters in two
  // places was the bug.
  //
  // dutyStatus is still required: an off-duty person is not on the map in
  // either mode, matching mobile's `.where('dutyStatus', isEqualTo: true)`.
  //
  // Reads through fetchUserProfile so a cross-organisation responder resolves
  // via getUserProfiles when the rules refuse users/{id} -- otherwise the read
  // that the org gate used to make unnecessary would now just fail instead.
  const fetchUserDetails = useCallback(async (userId) => {
    try {
      const userData = await fetchUserProfile(userId)
      if (!userData || userData.dutyStatus !== true) return null

      return {
        ...userData,
        id: userId,
        displayName: userData.displayName || `User ${userId}`,
        fullName: userData.fullName || '',
        userType: userData.userType || { color: '#3366ff' }
      }
    } catch (error) {
      console.error('Error fetching user details:', error)
      return null
    }
  }, [])

  useEffect(() => {
    if (!organizationId || !socket) return

    const handleUserLocationUpdate = async (data) => {
      if (
        !data?.userId ||
        !data?.latestPoint?.latitude ||
        !data?.latestPoint?.longitude
      )
        return

      // Fetch user details from Firebase
      const userDetails = await fetchUserDetails(data.userId)

      if (!userDetails) {
        return
      }

      // Transform socket data to match your existing format
      dispatch({
        type: DEVICE_LOCATION_EVENT,
        data: {
          id: data.userId,
          loc: [data.latestPoint.longitude, data.latestPoint.latitude],
          user: userDetails,
          timestamp: data.updatedAt || new Date().toISOString()
        }
      })
    }

    // Listen for user location updates
    socket.on('user_location_update', handleUserLocationUpdate)

    // Cleanup function
    return () => {
      socket.off('user_location_update', handleUserLocationUpdate)
    }
  }, [organizationId, socket, fetchUserDetails])

  // Clean up stale locations (users who haven't sent updates in 2 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now()
      const STALE_THRESHOLD = 120000 // 2 minutes

      Object.entries(deviceLocations).forEach(([userId, device]) => {
        if (device.timestamp) {
          const lastUpdate = new Date(device.timestamp).getTime()
          if (now - lastUpdate > STALE_THRESHOLD) {
            // Remove stale location
            dispatch({
              type: REINIT_STREAM_EVENT,
              data: { id: userId }
            })
          }
        }
      })
    }, 60000) // Check every minute

    return () => clearInterval(interval)
  }, [deviceLocations])

  return deviceLocations
}

// -------------------- INCIDENTS -------------------- //
const EXISTING_INCIDENTS_EVENT = 'exist'
const INCIDENT_CREATED_EVENT = 'new'
const INCIDENT_STATUS_CHANGED_EVENT = 'stat'
const DISPATCH_ACCEPTED_EVENT = 'accept'
const DISPATCH_CANCELED_EVENT = 'cancel'
const INCIDENT_ACKNOWLEDGED_EVENT = 'ack'
const INCIDENT_MARKER_CREATED_EVENT = 'mark'
const INCIDENT_MARKER_REMOVED_EVENT = 'unmark'

function reduceIncidents(state, { type, data }) {
  switch (type) {
    case REINIT_STREAM_EVENT:
      return {}
    case EXISTING_INCIDENTS_EVENT:
      return {
        ...state,
        ...data.items.reduce(
          (acc, incident) => ({
            ...acc,
            [incident.id]: { ...incident, dispatch: {} }
          }),
          {}
        )
      }
    case INCIDENT_CREATED_EVENT:
      return { ...state, [data.id]: { ...data, dispatch: {} } }
    case INCIDENT_STATUS_CHANGED_EVENT:
      return { ...state, [data.id]: { ...state[data.id]?.dispatch, ...data } }
    case DISPATCH_ACCEPTED_EVENT:
      return {
        ...state,
        [data.id]: {
          ...state[data.id],
          dispatch: {
            ...state[data.id]?.dispatch,
            [data.user.id]: { ...data.user, createdAt: data.createdAt }
          }
        }
      }
    case DISPATCH_CANCELED_EVENT:
      return {
        ...state,
        [data.id]: {
          ...state[data.id],
          dispatch: {
            ...state[data.id]?.dispatch,
            [data.user.id]: undefined
          }
        }
      }
    case INCIDENT_ACKNOWLEDGED_EVENT:
      return {
        ...state,
        [data.id]: {
          ...state[data.id],
          acknowledgements: [
            ...(state[data.id].acknowledgements || []),
            { user: data.user, createdAt: data.createdAt }
          ]
        }
      }
    case INCIDENT_MARKER_CREATED_EVENT:
      return {
        ...state,
        [data.id]: {
          ...state[data.id],
          mapMarkers: [...(state[data.id]?.mapMarkers || []), data.mapMarker]
        }
      }
    case INCIDENT_MARKER_REMOVED_EVENT:
      return {
        ...state,
        [data.id]: {
          ...state[data.id],
          mapMarkers:
            state[data.id]?.mapMarkers.filter(
              ({ id }) => id !== data.mapMarker.id
            ) || []
        }
      }
    default:
      return { ...state }
  }
}

// function useIncidentsList() {
//   const [incidents, dispatch] = useReducer(reduceIncidents, {})
//   const { user } = useContext(ApiContext);
//   const [profile] = useApiRequest(`users/${user?.id}`);

//   useEffect(() => {
//     const unsub = onSnapshot(collection(db, 'incidents'), (snapshot) => {
//       snapshot.docChanges().forEach((change) => {
//         const data = { id: change.doc.id, ...change.doc.data() }

//         let type = INCIDENT_CREATED_EVENT
//         if (change.type === 'modified') type = INCIDENT_STATUS_CHANGED_EVENT
//         if (change.type === 'removed') type = REINIT_STREAM_EVENT

//         dispatch({ type, data })
//       })
//     })

//     return () => unsub()
//   }, [])

//   return incidents
// }

function useIncidentsList(organizationId, user) {
  const [incidents, dispatch] = useReducer(reduceIncidents, {})

  useEffect(() => {
    if (!organizationId || !user?.id) return

    const incidentsCollection = collection(db, 'incidents')

    const q = query(
      incidentsCollection,
      or(
        where(
          'dispatchOrganizationsIdList',
          'array-contains',
          String(organizationId)
        ),
        where('createdBy', '==', user?.id)
      ),
      orderBy('createdAt', 'desc')
    )

    const unsub = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const data = { id: change.doc.id, ...change.doc.data() }

        let type = INCIDENT_CREATED_EVENT
        if (change.type === 'modified') type = INCIDENT_STATUS_CHANGED_EVENT
        if (change.type === 'removed') type = REINIT_STREAM_EVENT

        dispatch({ type, data })
      })
    })

    return () => unsub()
  }, [organizationId, user?.id])

  return incidents
}

// -------------------- USER AVAILABILITY -------------------- //
const USER_AVAILABILITY_EVENT = 'user'

function reduceAvailability(state, { type, data }) {
  switch (type) {
    case REINIT_STREAM_EVENT:
      return {}
    case USER_AVAILABILITY_EVENT:
      return { ...state, [data.user.id]: data.user }
    default:
      return { ...state }
  }
}

function useAvailability() {
  const [availableUnits, dispatch] = useReducer(reduceAvailability, {})

  useEffect(() => {
    let unsub = () => {}
    let cancelled = false
    ;(async () => {
      const q = await orgScopedUsersQuery()
      if (!q || cancelled) return
      unsub = onSnapshot(q, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const user = change.doc.data()
          if (user.dutyStatus !== undefined) {
            dispatch({
              type: USER_AVAILABILITY_EVENT,
              data: { user: { id: change.doc.id, ...user } }
            })
          }
        })
      })
    })()
    return () => {
      cancelled = true
      unsub()
    }
  }, [])

  return availableUnits
}

// -------------------- FIRESTORE API REQUESTS -------------------- //
async function fetchUserTypes() {
  const snap = await getDocs(collection(db, 'userTypes'))
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
}

async function fetchProfile(userId) {
  const docRef = doc(db, 'users', userId)
  const snap = await getDoc(docRef)
  return snap.exists() ? { id: snap.id, ...snap.data() } : null
}

async function fetchDispatchBindings(orgId) {
  try {
    const ref = collection(
      db,
      'organizations',
      String(orgId),
      'dispatch-bindings'
    )

    const snap = await getDocs(ref)
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }))

    return data
  } catch (err) {
    console.error('Error fetching dispatch bindings:', err)
    throw err
  }
}

// -------------------- MAIN PROVIDER -------------------- //
export default function ({ children }) {
  const [userTypes, setUserTypes] = React.useState(null)
  const { user } = useContext(ApiContext)

  const [profile, setProfile] = React.useState(null)
  const [dispatchBindings, setDispatchBindings] = React.useState(null)
  // const incidents = useIncidentsList()
  const availableUnits = useAvailability()
  const incidents = useIncidentsList(profile?.organization?.id, user)
  const deviceLocations = useDeviceLocations(profile?.organization?.id)

  useEffect(() => {
    ;(async () => {
      const types = await fetchUserTypes()
      setUserTypes(types)

      const auth = getAuth()
      const userId = auth.currentUser?.uid

      if (!userId) return

      const prof = await fetchProfile(userId)
      setProfile(prof)

      if (prof?.organization?.id) {
        const bindings = await fetchDispatchBindings(prof.organization.id)
        setDispatchBindings(bindings)
      }
    })()
  }, [])

  if (!userTypes || !dispatchBindings || !profile) {
    return <Spinner />
  }

  const props = {
    deviceLocations,
    incidents,
    availableUnits,
    userTypes,
    dispatchBindings,
    profile
  }

  return (
    <DispatchDataContext.Provider value={props}>
      {React.cloneElement(children, props)}
    </DispatchDataContext.Provider>
  )
}
